import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "@/providers/TranslationsProvider";

interface DropdownOption {
  value: string;
  label: string;
  flag?: string;
  /** Leading picture in place of an emoji `flag`, e.g. an SVG flag. */
  icon?: React.ReactNode;
}

interface DropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchable?: boolean;
  className?: string;
  disabled?: boolean;
  dir?: "ltr" | "rtl";
  /** Replaces the trigger's default look (border, fill, height), e.g. to match a form's pill fields. */
  triggerClassName?: string;
  /** Accessible name when no visible <label> points at the trigger. */
  ariaLabel?: string;
}

export default function Dropdown({
  options,
  value,
  onChange,
  placeholder = "Select...",
  searchable = false,
  className = "",
  disabled = false,
  dir,
  triggerClassName,
  ariaLabel,
}: DropdownProps) {
  const { t } = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isAbove, setIsAbove] = useState(false); 
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const filteredOptions = searchable
    ? options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      option.value.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : options;

  const closeDropdown = useCallback(() => {
    setIsAnimating(false);
    animationTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setSearchTerm("");
      setHighlightedIndex(-1);
    }, 200);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeDropdown]);

  const handleSelect = useCallback((optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm("");
    setHighlightedIndex(-1);
  }, [onChange]);

  const openDropdown = useCallback(() => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }

    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 300; 
      
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      let top = rect.bottom + 4;
      let above = false;
      
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        top = rect.top - Math.min(spaceAbove - 10, dropdownHeight) - 4;
        above = true;
      }
      
      setDropdownPosition({
        top,
        left: rect.left,
        width: Math.max(rect.width, 160)
      });
      setIsAbove(above);
    }
    
    setIsOpen(true);
    setIsAnimating(false);
    setTimeout(() => setIsAnimating(true), 0);
    if (searchable) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [searchable]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (disabled) return;

    switch (event.key) {
    case "ArrowDown":
      event.preventDefault();
      if (!isOpen) {
        openDropdown();
        setHighlightedIndex(0);
      } else {
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
      }
      break;
    case "ArrowUp":
      event.preventDefault();
      setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
      break;
    case "Enter":
      event.preventDefault();
      if (isOpen && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        handleSelect(filteredOptions[highlightedIndex].value);
      } else if (!isOpen) {
        openDropdown();
      }
      break;
    case "Escape":
      closeDropdown();
      break;
    }
  }, [isOpen, highlightedIndex, filteredOptions, disabled, handleSelect, openDropdown, closeDropdown]);

  const toggleDropdown = () => {
    if (disabled) return;
    if (isOpen) closeDropdown();
    else openDropdown();
  };

  const selectedOption = options.find(option => option.value === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`relative ps-3 pe-10 cursor-pointer transition-all duration-200 outline-none ${
          triggerClassName ?? `h-12 border rounded-2xl shadow-sm ${
            disabled
              ? "bg-black/[0.05] dark:bg-white/[0.05] border-transparent cursor-not-allowed opacity-60"
              : "bg-white dark:bg-white/5 border-black/[0.1] dark:border-white/[0.1] hover:border-[#0066CC] focus-within:border-[#0066CC] focus-within:ring-2 focus-within:ring-[#0066CC]/20"
          }`
        }`}
        dir={dir}
        aria-label={ariaLabel}
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls="dropdown-options"
      >
        <div className="flex items-center h-full">
          <span className={`truncate ${triggerClassName ? "" : "text-sm font-bold"} ${selectedOption ? 'text-[#1D1D1F] dark:text-white' : 'text-[#6E6E73] dark:text-white/40'}`}>
            {selectedOption ? (
              <div className="flex items-center">
                {selectedOption.icon ? <span className="me-2 inline-flex">{selectedOption.icon}</span> : selectedOption.flag && <span className="me-2 text-base">{selectedOption.flag}</span>}
                <span>{selectedOption.label}</span>
              </div>
            ) : (
              t("dropdown.select", placeholder)
            )}
          </span>
        </div>

        <div className="absolute inset-y-0 end-0 flex items-center pe-3 pointer-events-none">
          <svg
            className={`h-4 w-4 text-[#6E6E73] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {(isOpen || isAnimating) && createPortal(
        <div
          className={`
            fixed z-[99999] bg-white dark:bg-[#1D1D1F]
            border border-black/[0.05] dark:border-white/[0.1] rounded-2xl shadow-xl
            max-h-[300px] overflow-hidden
            transition-all duration-200 ease-out ${isAnimating ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 scale-95 ' + (isAbove ? 'translate-y-2' : '-translate-y-2')}
          `}
          dir={dir}
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`
          }}
        >
          {searchable && (
            <div className="p-2 border-b border-black/[0.05] dark:border-white/[0.05] bg-black/[0.02] dark:bg-white/[0.02]">
              <div className="relative">
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t("dropdown.search", "Search...")}
                  className="w-full px-3 py-2 text-sm border border-black/[0.1] dark:border-white/[0.1] rounded-xl bg-white dark:bg-black/[0.2] text-[#1D1D1F] dark:text-[#F5F5F7] placeholder-[#6E6E73] focus:outline-none focus:ring-2 focus:ring-[#0066CC]/20 focus:border-[#0066CC]"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )}

          <div id="dropdown-options" role="listbox" className="overflow-auto max-h-[250px] p-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const isSelected = value === option.value;
                return (
                  <div
                    key={option.value}
                    className={`px-3 py-2.5 rounded-xl cursor-pointer transition-colors flex items-center mb-0.5 last:mb-0 ${
                      index === highlightedIndex || isSelected
                        ? 'bg-[#0066CC]/10 text-[#0066CC] dark:text-[#F5F5F7]'
                        : 'text-[#1D1D1F] dark:text-[#F5F5F7]/80 hover:bg-black/[0.05] dark:hover:bg-white/10'
                    }`}
                    onClick={() => handleSelect(option.value)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {option.icon ? <span className="me-3 inline-flex">{option.icon}</span> : option.flag && <span className="me-3 text-base">{option.flag}</span>}
                    <span className="truncate flex-1 font-bold text-sm">{option.label}</span>
                    {isSelected && (
                      <svg className="ms-2 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-6 text-center text-[#6E6E73] uppercase tracking-widest text-[10px] font-bold">
                {t("dropdown.no options found", "No options found")}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}